/* BaseActivityViewColumnComponent.js */

"use strict";

const DefaultColumnComponent = require("@gluenetworks/steve_ui_widgets").GridGl.DefaultColumnComponent;
const _ = require("lodash");

class BaseActivityViewColumnComponent extends DefaultColumnComponent {
    constructor(activityEntity) {
        super(activityEntity);
        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    _normalizeCreateActivity(activityEntity) {
        let actvty = {};
        _.set(actvty, "activityType.activityType", "note");
        _.set(actvty, "activityType.tooltip", `Site created by (${activityEntity.createdBy}).`);
        _.set(actvty, "description.description", `Site created by user (${activityEntity.createdBy}).`);
        _.set(actvty, "status", "clean");
        return actvty;
    }

    _normalizeUpdateActivity(activityEntity) {
        let actvty = {};
        let jobName = activityEntity.getProperty("jobTypeName");
        jobName = _.titleize(_.humanize(jobName));
        if (jobName) {
            _.set(actvty, "activityType.activityType", "system");
            _.set(actvty, "activityType.tooltip", `Job (${jobName}) launched by ${activityEntity.createdBy}.`);
            _.set(actvty, "status", "clean");
            _.set(actvty, "description.description", `Site updated by job (${jobName}).`);
            let jobId = activityEntity.getProperty("jobId");
            _.set(actvty, "actions.viewMore", { logId: jobId });
        } else {
            _.set(actvty, "status", "clean");
            let activityDescription;
            if (!activityDescription) {
                let nodeLinkPatch = _.find(activityEntity.patch, { op: "add", path: "/nodeId" });
                if (nodeLinkPatch) {
                    _.set(actvty, "activityType.activityType", "system");
                    activityDescription = "A Node has been created from the Site.";
                }
            }
            if (!activityDescription) {
                let auditStatusPatch = _.find(activityEntity.patch, { path: "/auditStatus" });
                if (auditStatusPatch) {
                    let auditPolicy = _.get(
                        _.find(activityEntity.patch, { path: "/lastAuditPolicy" }),
                        "value",
                        _.get(activityEntity, ["before", "lastAuditPolicy"])
                    );
                    _.set(actvty, "activityType.activityType", "system");
                    switch (auditStatusPatch.value) {
                        case "PASSED":
                            activityDescription = `Site passed an Audit${auditPolicy ? ` (policy ${auditPolicy})` : ""}.`;
                            break;
                        case "FAILED":
                            activityDescription = `Site failed an Audit with one or more violations${auditPolicy ? ` (policy ${auditPolicy})` : ""}.`;
                            break;
                        case "ERROR":
                            let auditError = _.get(
                                _.find(activityEntity.patch, { path: "/lastActivityError" }),
                                "value",
                                _.get(activityEntity, ["before", "lastActivityError"])
                            );
                            activityDescription = `Site could not be audited${auditPolicy ? ` (policy ${auditPolicy})` : ""}: ${auditError}.`;
                            _.set(actvty, "status", "error");
                            break;
                    }
                }
            }
            if (!activityDescription) {
                _.set(actvty, "activityType.activityType", "note");
                activityDescription = `Site updated by (${activityEntity.createdBy}).`;
            }
            _.set(actvty, "activityType.tooltip", activityDescription);
            _.set(actvty, "description.description", activityDescription);
        }
        return actvty;
    }

    _normalizeJobActivity(activityEntity) {
        let jobName = activityEntity.getProperty("jobTypeName");
        jobName = _.titleize(_.humanize(jobName));
        let actvty = {};
        _.set(actvty, "activityType.activityType", "system");
        let error = activityEntity.getProperty("error");
        if (error) {
            _.set(
                actvty,
                "activityType.tooltip",
                `Job (${jobName}) launched by ${activityEntity.createdBy}&#013;&#013;${error.errorMsg} (${error.errorCode})`
            );
        } else {
            _.set(actvty, "activityType.tooltip", `Job (${jobName}) launched by ${activityEntity.createdBy}.`);
        }

        _.set(actvty, "status", "clean");

        let description = `Job (${jobName}) `;
        if (activityEntity.getProperty("jobStatus")) {
            switch (activityEntity.getProperty("jobStatus").toLowerCase()) {
                case "requested":
                    actvty.status = "queued";
                    description += "has been queued.";
                    break;
                case "queued":
                    actvty.status = "queued";
                    description += "has been queued.";
                    break;
                case "started":
                    actvty.status = "loading";
                    description += "has started.";
                    break;
                case "completed":
                    actvty.status = "clean";
                    description += "has completed successfully.";
                    break;
                case "cancelled":
                    actvty.status = "cancelled";
                    description += "was cancelled.";
                    break;
                case "failed":
                    actvty.status = "error";
                    description += "has failed.";
                    break;
                default:
                    actvty.status = "error";
                    description += "activity recorded.";
                    break;
            }
        }
        _.set(actvty, "description.description", description);
        let jobId = activityEntity.getProperty("jobId");
        if (!jobId) {
            jobId = activityEntity.getProperty("rEntityId");
        }
        _.set(actvty, "actions.viewMore", { logId: jobId });
        return actvty;
    }

    _normalizeJobRelationshipAddedActivity(activityEntity) {
        let jobName = activityEntity.getProperty("jobTypeName");
        jobName = _.titleize(_.humanize(jobName));
        let entityType = _.titleize(_.humanize(activityEntity.getProperty("rEntityType")));
        let relName = _.titleize(_.humanize(activityEntity.getProperty("relName")));
        let actvty = {};
        _.set(actvty, "activityType.activityType", "system");
        _.set(actvty, "activityType.tooltip", `Job (${jobName}) launched by ${activityEntity.createdBy}.`);
        _.set(actvty, "status", "clean");
        _.set(actvty, "description.description", `${entityType} has been added to the ${relName} relationship.`);
        let jobId = activityEntity.getProperty("jobId");
        _.set(actvty, "actions.viewMore", { logId: jobId });
        return actvty;
    }

    _normalizeJobRelationshipSetActivity(activityEntity) {
        let actvty = {};
        let activityDescription = "";
        if (activityEntity.relName === "stableSiteConfiguration") {
            _.set(actvty, "activityType.activityType", "system");
            activityDescription = "The default configuration of the Site has been set.";
        } else if (activityEntity.relName === "haPair") {
            _.set(actvty, "activityType.activityType", "note");
            if (_.get(activityEntity, "activityType", "") === "relRemove") {
                activityDescription = "Site removed its HA Pair relationship.";
            } else {
                activityDescription = "Site updated its HA Pair relationship.";
            }
        } else if (activityEntity.relName === "imageServer") {
            if (_.get(activityEntity, "activityType", "") === "relRemove") {
                activityDescription = "Site removed its File Server relationship.";
            } else {
                activityDescription = "Site updated its File Server relationship.";
            }
        } else {
            let entityType = _.titleize(_.humanize(activityEntity.getProperty("rEntityType")));
            let relName = _.titleize(_.humanize(activityEntity.getProperty("relName")));
            _.set(actvty, "activityType.activityType", "note");
            activityDescription = `${entityType} has been updated in the ${relName} relationship.`;
        }
        _.set(actvty, "activityType.tooltip", activityDescription);
        _.set(actvty, "description.description", activityDescription);
        _.set(actvty, "status", "clean");
        return actvty;
    }

    _normalizeUnknownActivity(activityEntity) {
        let actvty = {};
        _.set(actvty, "activityType.activityType", "unknown");
        _.set(actvty, "activityType.tooltip", `Unknown activity initiated by ${activityEntity.createdBy}.`);
        _.set(actvty, "description.description", "Unknown Activity");
        _.set(actvty, "status", "clean");
        return actvty;
    }

    _normalizeActivity(activityEntity) {
        let activity;
        let activityType = activityEntity.getProperty("activityType");
        switch (activityType) {
            case "create":
                activity = this._normalizeCreateActivity(activityEntity);
                break;
            case "update":
                activity = this._normalizeUpdateActivity(activityEntity);
                break;
            case "relSet":
                activity = this._normalizeJobRelationshipSetActivity(activityEntity);
                break;
            case "relAdd":
                if (activityEntity.getProperty("jobStatus")) {
                    activity = this._normalizeJobActivity(activityEntity);
                } else {
                    let jobName = activityEntity.getProperty("jobTypeName");
                    if (jobName) {
                        switch (jobName) {
                            case "captureSiteConfiguration":
                                activity = this._normalizeJobRelationshipAddedActivity(activityEntity);
                                break;
                        }
                    } else {
                        activity = this._normalizeJobRelationshipSetActivity(activityEntity);
                    }
                }
                break;
            case "relRemove":
                activity = this._normalizeJobRelationshipSetActivity(activityEntity);
                break;
            case "jobStatus":
                activity = this._normalizeJobActivity(activityEntity);
                break;
            // Hide unknown things from the user...
            // default:
            //    activity = this._normalizeUnknownActivity(activityEntity);
            // 	break;
        }
        if (activity) {
            let date =
                typeof activityEntity.createdOn !== "undefined"
                    ? new Date(activityEntity.createdOn).toLocaleDateString() + " " + new Date(activityEntity.createdOn).toLocaleTimeString()
                    : "No date time provided";
            _.set(activity, "snapshot.snapshot", date);
            _.set(activity, "id", activityEntity.id);
        }

        return activity;
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = BaseActivityViewColumnComponent;
